import {
  IPlayerGroup, IPlayerLayout, IPlayerMedia, IPlayerPlaylist,
  IPlayerPlaylistItem, IPlayerPlaylistResponse,
} from "./types";
import { clampDurationMs } from "./transitions";

/**
 * Pure walk of the playlist tree producing a flat, ordered sequence of
 * playable steps (media OR template). The renderer iterates this with
 * a wrap-around cursor so playback loops forever.
 *
 * Per spec: drive from playlist_group.playlists[] (not flat_playlist)
 * so per-playlist `duration_mode` is honored — though the wire-shape's
 * `is_active`/sort filtering is already done server-side, we re-walk
 * here defensively because we may be replaying from cache that was
 * stored before a server-side change.
 *
 * V2 — each playlist may carry `items[]` (mixed MEDIA + TEMPLATE) in
 * addition to the V1 `media[]` array. We prefer `items[]` when
 * present so TEMPLATE entries are visited in their real sort_order;
 * V1 caches fall back to `media[]` and continue to play media-only.
 */
/** Effective, ready-to-render transition (defaults already folded in). */
export interface StepTransition {
  id: string;
  durationMs: number;
  easing: string;
}

interface StepCommon {
  /** The item's own resolved transition (how it enters). */
  transition?: StepTransition;
  /**
   * Set only on the FIRST step of a playlist. It is the OUTGOING (previous)
   * playlist's "transition to next", and overrides the item transition when
   * playback crosses a playlist boundary — including the wrap-around from
   * the last playlist back to the first.
   */
  boundary?: StepTransition;
}

export type PlayerStep =
  | ({ kind: "media"; media: IPlayerMedia } & StepCommon)
  | ({ kind: "template"; templateId: number; durationSeconds: number; sortOrder: number } & StepCommon);

const DEFAULT_TRANSITION: StepTransition = { id: "cut", durationMs: 500, easing: "ease_in_out" };

function itemTransition(t?: {
  transition_in: string; transition_duration_ms: number; transition_easing: string;
} | null): StepTransition {
  if (!t) return DEFAULT_TRANSITION;
  return {
    id: t.transition_in || "cut",
    durationMs: t.transition_duration_ms || 500,
    easing: t.transition_easing || "ease_in_out",
  };
}

function playlistToNext(p: IPlayerPlaylist): StepTransition {
  return {
    id: p.transition_to_next || "cut",
    durationMs: p.transition_duration_ms || 500,
    easing: p.transition_easing || "ease_in_out",
  };
}

export function buildSteps(group: IPlayerGroup | null): PlayerStep[] {
  if (!group) return [];
  const steps: PlayerStep[] = [];
  const playlists = [...(group.playlists || [])]
    .filter(p => p.is_active !== false)
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));

  const N = playlists.length;
  playlists.forEach((p, pi) => {
    // The boundary transition to play when arriving at THIS playlist is the
    // previous playlist's transition_to_next; wrapping the first playlist
    // back to the last so a looped group animates the final→first jump.
    const boundary = N > 1 ? playlistToNext(playlists[(pi - 1 + N) % N]) : undefined;
    let first = true;
    const pushStep = (s: PlayerStep) => {
      if (first && boundary) s.boundary = boundary;
      first = false;
      steps.push(s);
    };

    if (p.items && p.items.length > 0) {
      const sorted = [...p.items].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
      for (const it of sorted) {
        if (it.item_type === "TEMPLATE") {
          pushStep({
            kind: "template",
            templateId: it.template_id,
            durationSeconds: it.duration ?? 10,
            sortOrder: it.sort_order ?? 0,
            transition: itemTransition(it.transition),
          });
        } else {
          pushStep({ kind: "media", media: it, transition: itemTransition(it.transition) });
        }
      }
    } else {
      const media = [...(p.media || [])]
        .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
      for (const m of media) {
        pushStep({ kind: "media", media: m, transition: itemTransition(m.transition) });
      }
    }
  });
  return steps;
}

/** The effective transition for entering a step (boundary overrides item). */
export function effectiveTransition(step: PlayerStep | undefined): StepTransition {
  if (!step) return DEFAULT_TRANSITION;
  return step.boundary ?? step.transition ?? DEFAULT_TRANSITION;
}

/**
 * V1 helper kept for callers that still want a flat media-only
 * sequence (e.g. prefetch). Internally re-uses buildSteps and drops
 * the TEMPLATE entries.
 */
export function buildSequence(group: IPlayerGroup | null): IPlayerMedia[] {
  return buildSteps(group)
    .flatMap(s => s.kind === "media" ? [s.media] : []);
}

/**
 * V2 — build a lookup so the renderer can resolve a TEMPLATE step's
 * `templateId` to its full layout payload in O(1).
 */
export function indexLayouts(
  response: IPlayerPlaylistResponse | null,
): Record<number, IPlayerLayout> {
  const out: Record<number, IPlayerLayout> = {};
  for (const l of response?.layouts ?? []) out[l.id] = l;
  return out;
}

/**
 * Compute the dwell time for an image item. Spec rule: use
 * media.duration seconds. Fall back to 10s when the server didn't
 * provide one (defensive — the API always sets it for images).
 */
export function imageDurationMs(media: IPlayerMedia): number {
  const secs = media.duration && media.duration > 0 ? media.duration : 10;
  return secs * 1000;
}

/**
 * Convenience for callers that still operate on a flat `media[]`
 * sequence — they can pass the matching playlist item index here.
 * V2 step variants use their own `durationSeconds`.
 */
export function stepDurationMs(step: PlayerStep): number {
  if (step.kind === "template") return step.durationSeconds * 1000;
  return imageDurationMs(step.media);
}

/* ──────────────────────────────────────────────────────────────────────── */
/* Shared timing model (spec §8)                                             */
/*                                                                          */
/* An item's configured duration is its TOTAL active time — the transition */
/* into the NEXT item plays during the item's final `tNext` ms, and the    */
/* next item becomes fully active exactly at the item's duration boundary.  */
/* The transition duration is never added on top of the item duration.      */
/* Used by BOTH the editor Preview and the production web Player so a        */
/* previewed cadence matches device playback.                               */
/* ──────────────────────────────────────────────────────────────────────── */

/**
 * The effective transition duration to actually animate, clamped so it can
 * never meet or exceed the item it plays over (spec §8). Cut / none / a
 * non-positive dwell collapse to an instant switch (0ms).
 */
export function effectiveTransitionMs(
  id: string | null | undefined,
  ms: number | null | undefined,
  dwellMs: number,
): number {
  if (!id || id === "cut" || id === "none") return 0;
  if (!Number.isFinite(dwellMs) || dwellMs <= 0) return 0;
  return Math.min(clampDurationMs(ms), Math.max(0, Math.round(dwellMs) - 1));
}

/**
 * Delay, measured from the moment THIS item's scene-switch begins, until the
 * switch to the NEXT item should fire.
 *
 * Timeline for one item:
 *   t=0            scene-switch begins (this item's own transition-in starts)
 *   t=tIn          this item is fully active
 *   t=tIn+dwell    the item's total active window ends (the boundary)
 * The next item's transition must FINISH at the boundary, so it must START
 * `tNext` before it → fire the next switch at `tIn + dwell - tNext`.
 *
 * All args in ms; result is floored at 0 so an over-long transition (already
 * clamped by effectiveTransitionMs) still advances.
 */
export function sceneSwitchDelayMs(dwellMs: number, tInMs: number, tNextMs: number): number {
  return Math.max(0, Math.round(tInMs + dwellMs - tNextMs));
}
